/**
 * Step Revealer Behavior
 *
 * Reveals the steps of `<x-ui.steps.group data-step-revealer>` in sequence when the group scrolls
 * into view: the circle scales up, the connector line grows, and the text slides up. The Web
 * Animations API drives every part (pseudo-elements included), so the whole sequence is one set
 * of compositor animations with no timers.
 *
 * Data-saver and slow connections show the content instantly.
 *
 * Attributes on the container:
 * - `data-threshold` — how much of the first step must be visible before the reveal starts (0-1, default 0.2)
 * - `data-viewport-trigger="false"` — do not start on scroll; wait for the trigger event
 *
 * Custom events (dispatched on the container element):
 * - 'step-revealer:trigger' — reset to hidden, then replay the reveal animation
 * - 'step-revealer:reset' — reset to hidden state (cancels any in-progress animation)
 */

import {createRun} from '../modules/motion/run.js';
import {shouldSaveData} from '../utils/networkInfo.js';

/** @type {boolean} */
const saveDataEnabled = shouldSaveData();

/**
 * @typedef {object} RevealerState
 * @property {IntersectionObserver|null} observer Watches the first step until the reveal starts.
 * @property {import('../modules/motion/run.js').Run} run The running reveal; a new one per replay.
 * @property {AbortController} abortController Removes container listeners on cleanup.
 */

/** @type {WeakMap<HTMLElement, RevealerState>} */
const stateMap = new WeakMap();

const STEP_DELAY_MS = 400;
const EASING = 'ease-out';

/** The hidden state the Blade component renders for `data-step-reveal` steps. */
const CIRCLE_HIDDEN_CLASS = 'before:scale-0';
const LINE_HIDDEN_CLASS = 'after:scale-y-0';
/** @type {readonly string[]} */
const CONTENT_HIDDEN_CLASSES = Object.freeze(['opacity-0', 'translate-y-20']);

/**
 * @typedef {object} StepPart
 * @property {Element} element The element that owns the animated box.
 * @property {string|undefined} pseudoElement `::before`, `::after`, or undefined for the element itself.
 * @property {Array<Record<string, string|number>>} keyframes From the hidden state to the natural state.
 * @property {number} duration Milliseconds.
 * @property {number} delay Offset from the step's own start.
 * @property {string[]} hiddenClasses Classes to remove from `element` when the part has animated in.
 */

/**
 * Get the direct child content wrapper div for a step element.
 * Uses :scope to avoid matching nested divs inside the step content.
 * @param {Element} step - The step <li> element
 * @returns {HTMLDivElement|null}
 */
function getStepContent(step) {
    return step.querySelector(':scope > div');
}

/**
 * @param {Element} step
 * @returns {StepPart[]}
 */
function getStepParts(step) {
    /** @type {StepPart[]} */
    const parts = [
        {
            element: step,
            pseudoElement: '::before',
            keyframes: [{scale: '0'}, {scale: '1'}],
            duration: 500,
            delay: 0,
            hiddenClasses: [CIRCLE_HIDDEN_CLASS],
        },
        {
            element: step,
            pseudoElement: '::after',
            keyframes: [{scale: '1 0'}, {scale: '1 1'}],
            duration: 700,
            delay: 150,
            hiddenClasses: [LINE_HIDDEN_CLASS],
        },
    ];

    const content = getStepContent(step);
    if (content) {
        parts.push({
            element: content,
            pseudoElement: undefined,
            keyframes: [{opacity: 0, translate: '0 20px'}, {opacity: 1, translate: '0 0'}],
            duration: 700,
            delay: 250,
            hiddenClasses: [...CONTENT_HIDDEN_CLASSES],
        });
    }

    return parts;
}

/**
 * @param {HTMLElement} container
 * @returns {Element[]}
 */
function getSteps(container) {
    return [...container.querySelectorAll('[data-step-reveal]')];
}

/**
 * Cancel every running animation of a container. The hidden classes stay as they are.
 * @param {HTMLElement} container
 */
function cancelAnimations(container) {
    const state = stateMap.get(container);
    if (!state) {
        return;
    }

    state.run.cancel();
    state.run = createRun();
}

/**
 * Show all steps immediately without animation.
 * @param {HTMLElement} container
 */
function showStepsImmediately(container) {
    cancelAnimations(container);

    for (const step of getSteps(container)) {
        for (const part of getStepParts(step)) {
            part.element.classList.remove(...part.hiddenClasses);
        }
    }
}

/**
 * Reset steps to the hidden state, cancelling any in-progress animation.
 * @param {HTMLElement} container
 */
function resetStepsToHidden(container) {
    cancelAnimations(container);

    for (const step of getSteps(container)) {
        for (const part of getStepParts(step)) {
            part.element.classList.add(...part.hiddenClasses);
        }
    }
}

/**
 * Reveal the steps one after another: circle, then line, then text.
 * @param {HTMLElement} container
 */
function revealSteps(container) {
    cancelAnimations(container);

    const {run} = stateMap.get(container);

    getSteps(container).forEach((step, index) => {
        for (const part of getStepParts(step)) {
            // Same task as the animation start, so the class removal and the first keyframe land in one style recalc.
            part.element.classList.remove(...part.hiddenClasses);
            run.add(part.element.animate(part.keyframes, {
                duration: part.duration,
                delay: index * STEP_DELAY_MS + part.delay,
                easing: EASING,
                // The first keyframe holds the hidden look through the delay. No forwards fill: a finished
                // animation leaves the natural styles, which equal the last keyframe, and the browser drops it.
                fill: 'backwards',
                pseudoElement: part.pseudoElement,
            }));
        }
    });
}

/**
 * Start the reveal when the first step scrolls into view.
 * @param {HTMLElement} container
 * @param {RevealerState} state
 */
function setupViewportObserver(container, state) {
    const observeTarget = container.querySelector('[data-step-reveal]') ?? container;
    let threshold = parseFloat(container.dataset.threshold || '0.2');
    if (Number.isNaN(threshold) || threshold < 0 || threshold > 1) {
        threshold = 0.2;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            if (stateMap.get(container) !== state || state.observer !== observer) {
                return;
            }
            if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= threshold)) {
                observer.disconnect();
                state.observer = null;
                revealSteps(container);
            }
        },
        {threshold}
    );

    state.observer = observer;
    observer.observe(observeTarget);
}

/**
 * @param {HTMLElement} container
 */
function initializeStepRevealer(container) {
    if (stateMap.has(container)) {
        return;
    }
    /** @type {RevealerState} */
    const state = {observer: null, run: createRun(), abortController: new AbortController()};
    stateMap.set(container, state);
    container.dataset.stepRevealerState = 'initialized';

    if (saveDataEnabled) {
        showStepsImmediately(container);
        return;
    }

    resetStepsToHidden(container);

    if (container.dataset.viewportTrigger !== 'false') {
        setupViewportObserver(container, state);
    }

    container.addEventListener('step-revealer:trigger', () => {
        state.observer?.disconnect();
        state.observer = null;
        resetStepsToHidden(container);
        revealSteps(container);
    }, {signal: state.abortController.signal});

    container.addEventListener('step-revealer:reset', () => {
        resetStepsToHidden(container);
    }, {signal: state.abortController.signal});
}

/**
 * Stop observing and animating, remove listeners, and show every step.
 * @param {HTMLElement} container
 */
export function cleanupStepRevealer(container) {
    const state = stateMap.get(container);
    if (!state) {
        return;
    }

    state.observer?.disconnect();
    state.abortController.abort();
    showStepsImmediately(container);
    stateMap.delete(container);
    delete container.dataset.stepRevealerState;
}

/**
 * Initialize every step revealer that is not initialized yet.
 */
export function initializeComponent() {
    for (const container of document.querySelectorAll('[data-step-revealer]')) {
        if (container instanceof HTMLElement) {
            initializeStepRevealer(container);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}

document.addEventListener('htmx:load', initializeComponent);
document.addEventListener('htmx:beforeCleanupElement', (event) => {
    if (event.target instanceof HTMLElement && event.target.matches('[data-step-revealer]')) {
        cleanupStepRevealer(event.target);
    }
});
